// src/pages1/Home.js
import DashboardCanvas from "../components1/DashboardCanvas";
import React, { useState, useEffect, useMemo, useRef } from "react";
import uuid from "react-uuid";
import { useLocation, useNavigate } from "react-router-dom";
import History from "./History";
import { getPersonalExpense, getExpenseCategory, getCurrency, API_BASE, API_HEADERS } from "../service/APIService";
import { ThemedDatePicker, ThemedMonthPicker } from "../components1/ThemedCalendar";
import HeroTrendChart from "../components1/HeroTrendChart";
import useSheetDismiss, { SHEET_CLOSE_MS } from "../utils/useSheetDismiss";
import { toLocalYMD } from "../utils/localDate";
import CategoryTrendDrawer from "../components1/CategoryTrendDrawer";
import CategoryDonut from "../components1/CategoryDonut";
import AddChoiceSheet from "../components1/AddChoiceSheet";
import VoiceAddSheet from "../components1/VoiceAddSheet";
import { isAIConfigured } from "../service/AIService";
import AnimatedCounter from "../components1/AnimatedCounter";


const CHART_COLORS = [
  "#42224A", // Deep Plum
  "#EF8767", // Vibrant Coral
  "#8F659A", // Rich Mauve
  "#2B7A78", // Deep Teal
  "#E66A46", // Terracotta Coral
  "#5C3566", // Royal Plum
  "#2563EB", // Royal Blue
  "#D97706", // Amber Gold
  "#059669", // Emerald Green
  "#9333EA", // Vivid Purple
  "#DC2626", // Crimson Red
  "#0D9488", // Dark Cyan
];

const PREDEFINED_CATEGORIES = [
  "Groceries",
  "Food",
  "Shopping",
  "Transport",
  "Subscription",
  "Rent",
  "Fuel",
  "Gift",
  "Travel",
  "Saving",
  "Healthcare",
  "Entertainment",
  "Other"
];

const getCategoryIcon = (category = "") => {
  const cat = category.toLowerCase();
  if (cat.includes("food") || cat.includes("rest") || cat.includes("drink") || cat.includes("cafe")) return "fi fi-rr-utensils";
  if (cat.includes("grocer") || cat.includes("supermarket")) return "fi fi-rr-shopping-bag";
  if (cat.includes("shop") || cat.includes("cloth") || cat.includes("shoe")) return "fi fi-rr-shop";
  if (cat.includes("travel") || cat.includes("fuel") || cat.includes("uber") || cat.includes("car") || cat.includes("transport")) return "fi fi-rr-car";
  if (cat.includes("rent") || cat.includes("home") || cat.includes("house")) return "fi fi-rr-home";
  if (cat.includes("sub") || cat.includes("stream") || cat.includes("tech") || cat.includes("apple") || cat.includes("phone")) return "fi fi-rr-laptop";
  if (cat.includes("health") || cat.includes("med") || cat.includes("doctor")) return "fi fi-rr-heart";
  if (cat.includes("gift") || cat.includes("party")) return "fi fi-rr-gift";
  if (cat.includes("save") || cat.includes("bank") || cat.includes("invest")) return "fi fi-rr-piggy-bank";
  return "fi fi-rr-receipt";
};

const ExpenseTrackerHome = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const descriptionRef = useRef(null);
  const [keyboardSpacer, setKeyboardSpacer] = useState(false);
  const { closing: addSheetClosing, requestClose: closeAddSheet } = useSheetDismiss(() => setShowAddSheet(false));
  const savedTimer = useRef(null);

  // View state: 'transactions' or 'report'
  const isReportInUrl = location.search.includes("view=report");
  const [activeView, setActiveView] = useState(isReportInUrl ? "report" : "transactions");

  // Bottom Sheet for Adding New Expense
  const isAddInUrl = location.search.includes("add=1") || location.search.includes("add=true");
  const [showAddSheet, setShowAddSheet] = useState(isAddInUrl);
  // the + offers voice or the form first; only where voice can actually run
  const [showAddChoice, setShowAddChoice] = useState(false);
  const [showVoiceSheet, setShowVoiceSheet] = useState(false);

  // User & Auth State
  const [loggedInUserEmail, setLoggedInUserEmail] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");

  const [fetchedCategories, setFetchedCategories] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedCategoryTrend, setSelectedCategoryTrend] = useState(null);

  const getCategoryMonthlyTrend = (categoryName) => {
    const today = new Date();
    const result = [];

    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const monthLabel = d.toLocaleString("en-US", { month: "short" });
      const yearMonth = `${year}-${String(month).padStart(2, "0")}`;

      const monthRows = expenses
        .filter((exp) => {
          if (!exp.date) return false;
          const [expY, expM] = exp.date.split("-").map(Number);
          const matchesDate = expY === year && expM === month;
          const matchesCat =
            (exp.category || "OTHER").trim().toLowerCase() ===
            (categoryName || "").trim().toLowerCase();
          return matchesDate && matchesCat;
        });

      const total = monthRows.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);

      result.push({
        month: monthLabel,
        yearMonth,
        amount: Math.round(total * 100) / 100,
        count: monthRows.length,
        items: monthRows
          .slice()
          .sort((a, b) => (a.date < b.date ? 1 : -1))
          .map((exp) => ({
            id: exp.id || `${exp.date}-${exp.description}-${exp.amount}`,
            date: exp.date,
            description: exp.description || exp.category || "Expense",
            amount: Number(exp.amount || 0),
          })),
      });
    }

    return result;
  };

  // Today formatted YYYY-MM-DD
  const getTodayDate = () => toLocalYMD(); // the phone's date, not UTC

  const [form, setForm] = useState({
    description: "",
    amount: "",
    category: PREDEFINED_CATEGORIES[0],
    date: getTodayDate(),
  });

  // Sync activeView with URL parameter
  useEffect(() => {
    if (location.search.includes("view=report")) {
      setActiveView("report");
    } else {
      setActiveView("transactions");
    }
  }, [location.search]);

  // Listen for FAB click event from BottomNav
  useEffect(() => {
    const handleOpenAddSheet = () => {
      if (isAIConfigured()) {
        setShowAddChoice(true);
      } else {
        setShowAddSheet(true);
      }
    };

    const handleManualRefresh = () => {
      const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const userEmail = localStorage.getItem("loggedInUserEmail");
      if (userEmail && userEmail !== "guest" && sessionUser?.email) {
        fetchCategories(sessionUser.email);
        fetchExpenses(sessionUser.email);
      } else {
        loadGuestExpenses();
      }
    };

    window.addEventListener("open-add-expense", handleOpenAddSheet);
    window.addEventListener("app-manual-refresh", handleManualRefresh);

  
  return () => {
      window.removeEventListener("open-add-expense", handleOpenAddSheet);
      window.removeEventListener("app-manual-refresh", handleManualRefresh);
    };
  }, []);

  // Fetch categories and expenses on mount
  useEffect(() => {
    const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
    const guestUser = JSON.parse(localStorage.getItem("guestUser"));
    const isRealUser = sessionUser && sessionUser.email && sessionUser.email !== "guest" && sessionUser.email.includes("@");

    if (isRealUser) {
      setEmail(sessionUser.email);
      setLoggedInUserEmail(sessionUser.email);
      setCountry(sessionUser.country || "");
      localStorage.setItem("loggedInUserEmail", sessionUser.email);
      localStorage.removeItem("guestUser");
      fetchCategories(sessionUser.email);
      fetchExpenses(sessionUser.email);
    } else if (guestUser && guestUser.email === "guest") {
      setLoggedInUserEmail("guest");
      setEmail(guestUser.email || "guest");
      setCountry(guestUser.country || "");
      loadGuestExpenses();
    } else {
      setLoggedInUserEmail("guest");
      loadGuestExpenses();
    }
  }, []);

  const fetchCategories = async (userEmail) => {
    try {
      const response = await getExpenseCategory(userEmail);
      if (Array.isArray(response)) {
        setFetchedCategories(
          response
            .filter((item) => item.type === "expense-category")
            .map((item) => item.category)
        );
      }
    } catch (err) {
      console.warn("Could not fetch custom categories:", err);
    }
  };

  // Rows written a moment ago may be missing from the next read (the table is
  // eventually consistent), so keep any pending row until the server reports it.
  const pendingExpensesRef = useRef([]);

  const fetchExpenses = async (userEmail) => {
    setLoading(true);
    try {
      const response = await getPersonalExpense(userEmail);
      if (Array.isArray(response)) {
        const known = new Set(response.map((r) => r && r.id));
        pendingExpensesRef.current = pendingExpensesRef.current.filter((p) => !known.has(p.id));
        setExpenses(pendingExpensesRef.current.length ? [...pendingExpensesRef.current, ...response] : response);
      }
    } catch (err) {
      console.error("Failed to fetch expenses:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadGuestExpenses = () => {
    let guestExpenses = JSON.parse(localStorage.getItem("guestExpenses"));
    if (!guestExpenses || guestExpenses.length === 0) {
      const today = new Date();
      const formatYMD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      const starterData = [
        {
          id: "starter-1",
          description: "Nike Store",
          amount: 734.0,
          category: "SHOPPING",
          date: formatYMD(today),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-2",
          description: "Apple Store",
          amount: 25.0,
          category: "SUBSCRIPTION",
          date: formatYMD(today),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-3",
          description: "Uber Ride",
          amount: 4.99,
          category: "TRANSPORT",
          date: formatYMD(today),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-4",
          description: "Whole Foods Market",
          amount: 142.5,
          category: "GROCERIES",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-5",
          description: "Starbucks Coffee",
          amount: 14.2,
          category: "FOOD",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2)),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-6",
          description: "Rent & Utilities",
          amount: 850.0,
          category: "RENT",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 1, 15)),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-7",
          description: "Shopping Mall",
          amount: 320.0,
          category: "SHOPPING",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 2, 10)),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-8",
          description: "Weekend Trip",
          amount: 450.0,
          category: "TRAVEL",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 3, 5)),
          email: "guest",
          expensetype: "personal",
        },
        {
          id: "starter-9",
          description: "Electronics & Tech",
          amount: 280.0,
          category: "SHOPPING",
          date: formatYMD(new Date(today.getFullYear(), today.getMonth() - 4, 20)),
          email: "guest",
          expensetype: "personal",
        },
      ];
      localStorage.setItem("guestExpenses", JSON.stringify(starterData));
      guestExpenses = starterData;
    }
    setExpenses(guestExpenses);
  };

  // Sort categories by frequency of usage in expenses (most frequent first)
  const sortedCategories = useMemo(() => {
    const counts = {};
    expenses.forEach((exp) => {
      const cat = (exp.category || "").trim();
      if (cat) {
        counts[cat.toLowerCase()] = (counts[cat.toLowerCase()] || 0) + 1;
      }
    });

    const all = Array.from(new Set([...PREDEFINED_CATEGORIES, ...fetchedCategories]));
    return all.sort((a, b) => {
      const countA = counts[a.toLowerCase()] || 0;
      const countB = counts[b.toLowerCase()] || 0;
      if (countB !== countA) return countB - countA;
      return a.localeCompare(b);
    });
  }, [expenses, fetchedCategories]);

  const combinedCategories = sortedCategories;

  // Frequently used categories (first 2 lines in 2-column layout: 4 items) vs all
  const FREQUENT_LIMIT = 4;
  const visibleCategories = useMemo(() => {
    if (showAllCategories) return sortedCategories;
    const top = sortedCategories.slice(0, FREQUENT_LIMIT);
    // Ensure currently selected category is included in the visible list while keeping 4 items
    if (form.category && !top.some((c) => c.toLowerCase() === form.category.toLowerCase())) {
      if (top.length >= FREQUENT_LIMIT) {
        return [...top.slice(0, FREQUENT_LIMIT - 1), form.category];
      } else {
        return [...top, form.category];
      }
    }
    return top;
  }, [sortedCategories, showAllCategories, form.category]);

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "amount" && value < 0 ? 0 : value,
    }));
  };

  const handleCategorySelect = (categoryName) => {
    setForm((prev) => ({ ...prev, category: categoryName }));
  };

  const showSavedNotification = () => {
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 2000);
  };

  const isExpenseFormValid = Boolean(form.description.trim()) && Boolean(form.amount) && Number(form.amount) > 0;

  // iOS shrinks the viewport for the keyboard but leaves the focused field wherever
  // it was; pull it back into the middle of what is still visible.
  const keepFieldVisible = (e) => {
    const el = e.target;
    const sheet = el.closest && el.closest(".bottom-sheet-content");
    if (!sheet) return;
    setKeyboardSpacer(true); // lets even the first field scroll up to the keyboard
    setTimeout(() => {
      try {
        // never scrollIntoView here: on iOS that scrolls the page as well and
        // drags the pinned sheet up to the top edge.
        const elRect = el.getBoundingClientRect();
        const elTop = elRect.top - sheet.getBoundingClientRect().top;
        // sit the field just above the bottom edge of the sheet, i.e. just above
        // the keyboard, rather than leaving it stranded at the top.
        const target = sheet.scrollTop + elTop - (sheet.clientHeight - elRect.height - 24);
        sheet.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
        window.scrollTo(0, 0); // undo any scroll iOS did to reveal the field
      } catch {
        /* older browsers ignore the options object */
      }
    }, 300);
  };

  const releaseFieldSpacer = () => {
    // collapse once focus has left every field in the sheet
    setTimeout(() => {
      const active = document.activeElement;
      if (!active || !active.closest || !active.closest(".bottom-sheet-content")) {
        setKeyboardSpacer(false);
      }
    }, 150);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    if (!form.description) {
      alert("Please enter an expense description.");
      return;
    }
    await saveExpense(form, closeAddSheet);
  };

  // Shared by the form and by the voice sheet, so a spoken expense is written
  // exactly the way a typed one is.
  const saveExpense = async (fields, dismissSheet) => {
    const newExpense = {
      id: uuid(),
      type: "expense",
      description: fields.description,
      amount: parseFloat(fields.amount),
      category: String(fields.category || "").toUpperCase(),
      date: fields.date,
      email: email,
      expensetype: "personal",
    };

    setLoading(true);
    try {
      if (loggedInUserEmail !== "guest") {
        const res = await fetch(`${API_BASE}/items`, {
          method: "POST",
          headers: API_HEADERS,
          body: JSON.stringify(newExpense),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        showSavedNotification();
        pendingExpensesRef.current = [newExpense, ...pendingExpensesRef.current];
        setExpenses((prev) => [newExpense, ...prev]);
        fetchExpenses(email);
      } else {
        const guestExpenses = JSON.parse(localStorage.getItem("guestExpenses")) || [];
        guestExpenses.push(newExpense);
        localStorage.setItem("guestExpenses", JSON.stringify(guestExpenses));
        loadGuestExpenses();
        showSavedNotification();
      }
      setForm({
        description: "",
        amount: "",
        category: combinedCategories[0] || PREDEFINED_CATEGORIES[0],
        date: getTodayDate(),
      });
      dismissSheet();
      // bring the chart back into view so the new expense is visible on it
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
          window.scrollTo(0, 0);
        }
      }, SHEET_CLOSE_MS + 160); // after the sheet has slid away and unlocked the page
    } catch (err) {
      console.error("Failed to save expense:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculations
  const totalExpense = useMemo(() => {
    return expenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
  }, [expenses]);

  // Dynamic multi-month history for the mini bar chart inside hero card (allows horizontal scrolling)
  const miniBarData = useMemo(() => {
    const today = new Date();
    let monthsBack = 11; // 12 months by default
    expenses.forEach((exp) => {
      if (exp.date) {
        const [y, m] = exp.date.split("-").map(Number);
        const diffMonths = (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m);
        if (diffMonths > monthsBack && diffMonths < 60) {
          monthsBack = diffMonths;
        }
      }
    });

    const months = [];
    for (let i = monthsBack; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const monthStr = `${year}-${String(month).padStart(2, "0")}`;
      const label = d.toLocaleString("en-US", { month: "short" });

      const total = expenses
        .filter((exp) => {
          if (!exp.date) return false;
          const [expY, expM] = exp.date.split("-").map(Number);
          return expY === year && expM === month;
        })
        .reduce((sum, exp) => sum + Number(exp.amount || 0), 0);

      months.push({
        label,
        yearMonth: monthStr,
        total,
        isCurrent: i === 0,
      });
    }

    const maxTotal = Math.max(...months.map((m) => m.total), 1);
    return months.map((m) => ({
      ...m,
      heightPercent: Math.max(12, Math.round((m.total / maxTotal) * 100)),
    }));
  }, [expenses]);

  // Month options for filtering report view
  const monthOptions = useMemo(() => {
    const months = new Set();
    expenses.forEach((expense) => {
      if (expense.date) {
        const [year, month] = expense.date.split("-");
        months.add(`${year}-${month}`);
      }
    });

    const options = Array.from(months)
      .sort((a, b) => new Date(b) - new Date(a))
      .map((ym) => {
        const [year, month] = ym.split("-").map(Number);
        const date = new Date(year, month - 1, 1);
        return {
          value: ym,
          label: date.toLocaleString("en-US", { month: "long", year: "numeric" }),
        };
      });

    return [
      { value: "current", label: "Current Month" },
      { value: "all", label: "All Time" },
      ...options,
    ];
  }, [expenses]);

  // Expenses filtered by selectedMonth for the Report view
  const reportFilteredExpenses = useMemo(() => {
    if (selectedMonth === "all") return expenses;

    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;

    const [filterYear, filterMonth] =
      selectedMonth === "current"
        ? [curYear, curMonth]
        : selectedMonth.split("-").map(Number);

    return expenses.filter((expense) => {
      if (!expense.date) return false;
      const [y, m] = expense.date.split("-").map(Number);
      return y === filterYear && m === filterMonth;
    });
  }, [selectedMonth, expenses]);

  const reportTotal = useMemo(() => {
    return reportFilteredExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  }, [reportFilteredExpenses]);

  // Category breakdown for Donut Chart & Progress bars
  // The month the report is filtered to, as YYYY-MM — the category table
  // highlights it and opens on it. Null while viewing All Time.
  const activeYearMonth = useMemo(() => {
    if (selectedMonth === "all") return null;
    if (selectedMonth === "current") {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    }
    return selectedMonth;
  }, [selectedMonth]);

  const categoryBreakdown = useMemo(() => {
    if (reportTotal === 0) return [];
    const grouped = reportFilteredExpenses.reduce((acc, exp) => {
      const cat = exp.category || "OTHER";
      acc[cat] = (acc[cat] || 0) + Number(exp.amount || 0);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], index) => {
        const percent = Math.round((value / reportTotal) * 1000) / 10;
        return {
          name,
          value,
          percent,
          color: CHART_COLORS[index % CHART_COLORS.length],
        };
      });
  }, [reportFilteredExpenses, reportTotal]);

  const switchView = (view) => {
    setActiveView(view);
    if (view === "report") {
      navigate("/app1/home?view=report", { replace: true });
    } else {
      navigate("/app1/home", { replace: true });
    }
  };

  return (
    <div className="px-3 pt-2">
      <DashboardCanvas theme="home" />
      {/* Segmented Toggle: Transactions / Reports (matching Image 2) */}
      <div className="segmented-control">
        <div
          className="segmented-slider"
          style={{
            transform: activeView === "report" ? "translateX(100%)" : "translateX(0%)",
          }}
          aria-hidden="true"
        />
        <button
          className={`segmented-btn ${activeView === "transactions" ? "active" : ""}`}
          onClick={() => switchView("transactions")}
        >
          Transactions
        </button>
        <button
          className={`segmented-btn ${activeView === "report" ? "active" : ""}`}
          onClick={() => switchView("report")}
        >
          Expenses Report
        </button>
      </div>

      {activeView === "transactions" ? (
        /* ==================== TRANSACTIONS VIEW ==================== */
        <div>
          {/* Hero Outcome/Balance Card with Mini Bar Chart (Image 1 & 2) */}
          <div className="hero-balance-card hero-balance-card-home">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  className="d-flex align-items-center gap-2 mb-2 flex-nowrap"
                  style={{ minWidth: 0 }}
                >
                  <span
                    className="hero-balance-label mb-0"
                    style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Outcome
                  </span>
                  <div style={{ flexShrink: 0 }}>
                    <ThemedMonthPicker
                      value={selectedMonth}
                      onChange={setSelectedMonth}
                      options={monthOptions}
                      variant="glass"
                    />
                  </div>
                </div>
                <div className="hero-balance-amount">
                  <AnimatedCounter
                    value={reportTotal}
                    currency={getCurrency(country)}
                  />
                </div>
              </div>

              <button
                className="header-icon-btn"
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "#FFFFFF",
                  border: "none",
                }}
                onClick={() => setShowAddSheet(true)}
                title="Add Expense"
              >
                <i className="fi fi-rr-plus"></i>
              </button>
            </div>

            {/* Mini Bar + Curved Trend Line Chart */}
            <HeroTrendChart
              data={miniBarData}
              currency={getCurrency(country)}
              selectedMonth={selectedMonth}
              onSelectMonth={(ym) => setSelectedMonth(ym)}
            />
          </div>

          {/* Quick Metrics Cards (Image 2) */}
          <div className="metrics-row">
            <div className="metric-card">
              <div className="metric-icon-wrap metric-icon-expense">
                <i className="fi fi-rr-wallet"></i>
              </div>
              <div className="metric-info">
                <span className="metric-label">All-Time Total</span>
                <span className="metric-value">
                  {getCurrency(country)}
                  {totalExpense.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon-wrap metric-icon-income">
                <i className="fi fi-rr-layers"></i>
              </div>
              <div className="metric-info">
                <span className="metric-label">Transactions</span>
                <span className="metric-value">{expenses.length} records</span>
              </div>
            </div>
          </div>

          {/* Transaction Feed */}
          <History
            expenses={expenses}
            country={country}
            isGuest={loggedInUserEmail === "guest"}
            onChanged={() =>
              loggedInUserEmail === "guest" ? loadGuestExpenses() : fetchExpenses(email)
            }
            categories={combinedCategories}
          />
        </div>
      ) : (
        /* ==================== EXPENSES REPORT VIEW (Image 2 Screen 2) ==================== */
        <div>
          {/* Report Month Picker */}
          <div className="d-flex justify-content-between align-items-center mb-3 dash-report-head">
            <div>
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                Analytics
              </h5>
              <small className="text-muted" style={{ fontSize: "11.5px" }}>
                Spending by category
              </small>
            </div>

            <ThemedMonthPicker
              value={selectedMonth}
              onChange={setSelectedMonth}
              options={monthOptions}
              variant="light"
              showArrows={true}
            />
          </div>

          {/* Interactive Donut Chart */}
          <CategoryDonut
            data={categoryBreakdown}
            total={reportTotal}
            currency={getCurrency(country)}
          />

          {/* Category Breakdown Progress Bars (Image 2 Screen 2) */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 className="fw-bold mb-0" style={{ fontSize: "15px", color: "var(--primary-dark)" }}>
                  All Categories
                </h6>
                <small className="text-muted" style={{ fontSize: "11px" }}>
                  Tap any category to view monthly curved trend
                </small>
              </div>
            </div>

            {categoryBreakdown.length === 0 ? (
              <p className="text-muted small text-center py-4">No expenses recorded for this period.</p>
            ) : (
              categoryBreakdown.map((cat, idx) => {
                const iconClass = getCategoryIcon(cat.name);
                const isExpanded = selectedCategoryTrend === cat.name;
                const trendData = isExpanded ? getCategoryMonthlyTrend(cat.name) : [];
                const gradId = `homeCatGrad_${idx}_${encodeURIComponent(cat.name).replace(/[^a-zA-Z0-9]/g, "_")}`;

                return (
                  <div
                    key={idx}
                    className={`category-report-card ${isExpanded ? "expanded" : ""}`}
                    onClick={() =>
                      setSelectedCategoryTrend((prev) => (prev === cat.name ? null : cat.name))
                    }
                  >
                    <div className="category-report-header">
                      <div className="category-report-title">
                        <div
                          className="category-icon-circle"
                          style={{
                            width: "36px",
                            height: "36px",
                            fontSize: "16px",
                            backgroundColor: `${cat.color}15`,
                            color: cat.color,
                          }}
                        >
                          <i className={iconClass}></i>
                        </div>
                        <div>
                          <div style={{ lineHeight: 1.2 }}>{cat.name}</div>
                          <span className="category-report-percent">{cat.percent}% of total</span>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <div className="category-report-amount">
                          {getCurrency(country)}
                          {cat.value.toFixed(2)}
                        </div>
                        <i
                          className={`fi fi-rr-angle-small-${isExpanded ? "up" : "down"}`}
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "16px",
                            transition: "transform 0.2s ease",
                          }}
                        ></i>
                      </div>
                    </div>

                    <div className="category-progress-track">
                      <div
                        className="category-progress-fill"
                        style={{
                          width: `${cat.percent}%`,
                          backgroundColor: cat.color,
                        }}
                      ></div>
                    </div>

                    {/* Expandable Monthly Curved Line Trend Graph */}
                    {isExpanded && (
                      <CategoryTrendDrawer
                        cat={cat}
                        trendData={trendData}
                        activeYearMonth={activeYearMonth}
                        currency={getCurrency(country)}
                        gradId={gradId}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}


      {/* ==================== VOICE OR FORM, OFFERED AT THE + ==================== */}
      {showAddChoice && (
        <AddChoiceSheet
          onVoice={() => {
            setShowAddChoice(false);
            setShowVoiceSheet(true);
          }}
          onManual={() => {
            setShowAddChoice(false);
            setShowAddSheet(true);
          }}
          onClose={() => setShowAddChoice(false)}
        />
      )}

      {showVoiceSheet && (
        <VoiceAddSheet
          categories={combinedCategories}
          currency={getCurrency(country)}
          scope="personal"
          todayLocal={getTodayDate()}
          onSave={(draft) => saveExpense(draft, () => setShowVoiceSheet(false))}
          onClose={() => setShowVoiceSheet(false)}
          onManual={() => {
            setShowVoiceSheet(false);
            setShowAddSheet(true);
          }}
        />
      )}

      {/* ==================== ADD EXPENSE SLIDE-UP BOTTOM SHEET ==================== */}
      {showAddSheet && (
        <div className="bottom-sheet-overlay" onClick={closeAddSheet}>
          <div className={`bottom-sheet-content${addSheetClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="bottom-sheet-handle" onClick={closeAddSheet}></div>
            <button
              type="button"
              className="bottom-sheet-close-corner"
              onClick={closeAddSheet}
              aria-label="Close"
              title="Close"
            >
              <i className="fi fi-rr-cross"></i>
            </button>

            <div className="mb-3">
              <h5 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                Add New Expense
              </h5>
            </div>

            <form onSubmit={handleFormSubmit}>
              {/* Big Amount Input */}
              <div className="modern-input-group mb-3">
                <label>Amount{getCurrency(country) ? ` (${getCurrency(country)})` : ""}</label>
                <input
                  type="number"
                  name="amount"
                  placeholder="0.00"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  className="modern-input modern-input-amount"
                  value={form.amount}
                  onChange={handleInputChange}
                  onFocus={keepFieldVisible}
                  onBlur={releaseFieldSpacer}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (descriptionRef.current) descriptionRef.current.focus();
                    }
                  }}
                  enterKeyHint="next"
                  autoFocus
                  required
                />
              </div>

              {/* Description Input (required) */}
              <div className="modern-input-group mb-3">
                <label>
                  Description <span style={{ color: "#E5484D" }}>*</span>
                </label>
                <input
                  ref={descriptionRef}
                  type="text"
                  name="description"
                  placeholder="e.g. Nike Store, Groceries, Dinner"
                  className={`modern-input${descriptionTouched && !form.description.trim() ? " is-invalid-field" : ""}`}
                  value={form.description}
                  onChange={handleInputChange}
                  onBlur={() => {
                    setDescriptionTouched(true);
                    releaseFieldSpacer();
                  }}
                  onFocus={keepFieldVisible}
                  required
                />
                {descriptionTouched && !form.description.trim() && (
                  <small style={{ color: "#E5484D" }}>Description is required</small>
                )}
              </div>

              {/* Category Chips (Frequently used in 2 lines + Show All) */}
              <div className="modern-input-group mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="mb-0" style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-muted)" }}>
                    Category {!showAllCategories && <span style={{ fontWeight: 400, opacity: 0.8 }}>(Frequently Used)</span>}
                  </label>
                  {sortedCategories.length > FREQUENT_LIMIT && (
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0"
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--accent-coral)",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowAllCategories(!showAllCategories)}
                    >
                      {showAllCategories ? "Show Less ▴" : `Show All (${sortedCategories.length}) ▾`}
                    </button>
                  )}
                </div>

                <div className="category-2col-grid">
                  {visibleCategories.map((cat, idx) => {
                    const isSelected = form.category.toLowerCase() === cat.toLowerCase();
                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`category-2col-btn ${isSelected ? "active" : ""}`}
                        onClick={() => handleCategorySelect(cat)}
                      >
                        <i className={getCategoryIcon(cat)}></i>
                        <span>{cat}</span>
                      </button>
                    );
                  })}

                  {!showAllCategories && sortedCategories.length > FREQUENT_LIMIT && (
                    <button
                      type="button"
                      className="category-2col-more-btn"
                      onClick={() => setShowAllCategories(true)}
                      title="Show all categories"
                    >
                      <span>+ More ({sortedCategories.length - FREQUENT_LIMIT})</span>
                      <i className="fi fi-rr-angle-small-down"></i>
                    </button>
                  )}

                  {showAllCategories && sortedCategories.length > FREQUENT_LIMIT && (
                    <button
                      type="button"
                      className="category-2col-more-btn"
                      onClick={() => setShowAllCategories(false)}
                      title="Show fewer categories"
                    >
                      <span>Show Less</span>
                      <i className="fi fi-rr-angle-small-up"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Themed Date Picker */}
              <div className="modern-input-group mb-4">
                <ThemedDatePicker
                  value={form.date}
                  onChange={handleInputChange}
                  name="date"
                  label="Date"
                  required
                />
              </div>

              {/* Submit CTA Button */}
              <button
                type="submit"
                className="btn-coral-modern"
                disabled={loading || !isExpenseFormValid}
              >
                {saved ? (
                  <>
                    <i className="fi fi-rr-check me-2"></i> Saved Successfully!
                  </>
                ) : loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i> Adding...
                  </>
                ) : (
                  <>
                    <i className="fi fi-rr-plus me-2"></i> Save Expense
                  </>
                )}
              </button>

              {/* Room to scroll the focused field above the keyboard */}
              <div aria-hidden="true" style={{ height: keyboardSpacer ? "42vh" : 0, transition: "height 0.2s ease" }} />
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseTrackerHome;
